// ============================================
// CONFIGURAÇÕES CENTRALIZADAS DE PERFORMANCE
// ============================================

/**
 * Tempos de cache por tipo de dado
 * Dados que mudam raramente = staleTime maior
 * Dados que mudam frequentemente = staleTime menor
 */
export const CACHE_TIMES = {
  // Dados do usuário (mudam muito raramente)
  USER_PROFILE: 10 * 60 * 1000,      // 10 minutos
  USER_PERMISSIONS: 10 * 60 * 1000,  // 10 minutos
  USER_SESSION: 10 * 60 * 1000,      // 10 minutos
  
  // Configurações do sistema (mudam raramente)
  SYSTEM_CONFIG: 5 * 60 * 1000,      // 5 minutos
  TEAMS: 5 * 60 * 1000,              // 5 minutos
  QUEUES: 5 * 60 * 1000,             // 5 minutos
  PRODUCTS: 5 * 60 * 1000,           // 5 minutos
  STATUS_OPTIONS: 5 * 60 * 1000,     // 5 minutos
  
  // Listas e dados operacionais (mudam com frequência moderada)
  LEADS: 2 * 60 * 1000,              // 2 minutos
  ORDERS: 2 * 60 * 1000,             // 2 minutos
  MESSAGES: 30 * 1000,               // 30 segundos (realtime)
  
  // Dados com realtime ativo (cache curto, realtime atualiza)
  REALTIME_DATA: 30 * 1000,          // 30 segundos
  
  // Garbage collection (quando remover do cache)
  GC_TIME: 15 * 60 * 1000,           // 15 minutos
} as const;

/**
 * Configuração padrão do React Query otimizada para performance
 */
export const DEFAULT_QUERY_OPTIONS = {
  staleTime: CACHE_TIMES.SYSTEM_CONFIG,
  gcTime: CACHE_TIMES.GC_TIME,
  retry: 1,
  refetchOnWindowFocus: false,
  refetchOnMount: false,
  refetchOnReconnect: false,
} as const;

/**
 * Opções para queries de dados do usuário (cache longo)
 */
export const USER_QUERY_OPTIONS = {
  staleTime: CACHE_TIMES.USER_PROFILE,
  gcTime: CACHE_TIMES.GC_TIME,
  retry: 2,
  refetchOnWindowFocus: false,
  refetchOnMount: false,
} as const;

/**
 * Opções para queries de listas (cache moderado)
 */
export const LIST_QUERY_OPTIONS = {
  staleTime: CACHE_TIMES.LEADS,
  gcTime: CACHE_TIMES.GC_TIME,
  retry: 1,
  refetchOnWindowFocus: false,
} as const;

/**
 * Opções para queries com realtime (cache curto)
 */
export const REALTIME_QUERY_OPTIONS = {
  staleTime: CACHE_TIMES.REALTIME_DATA,
  gcTime: CACHE_TIMES.GC_TIME,
  retry: 1,
  refetchOnWindowFocus: false,
} as const;

/**
 * Opções específicas para TV Dashboards (cache longo, sem realtime redundante)
 * TV usa useTvAutoRefresh para refresh periódico, não precisa de refetchInterval
 */
export const TV_QUERY_OPTIONS = {
  staleTime: 2 * 60 * 1000,           // 2 minutos
  gcTime: 10 * 60 * 1000,             // 10 minutos
  retry: 1,
  refetchOnWindowFocus: false,
  refetchOnMount: false,
  refetchOnReconnect: false,
} as const;

/**
 * Opções para Dashboards interativos (usuário ativo navegando)
 * Cache de 5 minutos, sem refetch automático em foco
 */
export const DASHBOARD_QUERY_OPTIONS = {
  staleTime: 5 * 60 * 1000,           // 5 minutos
  gcTime: 15 * 60 * 1000,             // 15 minutos
  retry: 1,
  refetchOnWindowFocus: false,
  refetchOnMount: 'always' as const,
} as const;

/**
 * Opções para dashboards SLA (cache moderado)
 */
export const SLA_QUERY_OPTIONS = {
  staleTime: 5 * 60 * 1000,           // 5 minutos
  gcTime: 15 * 60 * 1000,             // 15 minutos
  retry: 1,
  refetchOnWindowFocus: false,
} as const;

// ============================================
// LEI DE PERFORMANCE - CLASSIFICAÇÃO DE PESO
// ============================================

/**
 * Intervalos mínimos de polling por peso de query.
 * PESO A: tabelas simples, índices claros, sem RLS complexa
 * PESO B: joins moderados, RLS, limit <= 200
 * PESO C: views pesadas (v_planilha_mae, v_leads_carteira_supervisor, v_central_faturas)
 */
export const POLLING_INTERVALS = {
  PESO_A: 60 * 1000,        // 1 minuto
  PESO_B: 2 * 60 * 1000,    // 2 minutos
  PESO_C: 5 * 60 * 1000,    // 5 minutos
} as const;

/**
 * Configuração do Economy Mode global.
 * Ativado quando queries PESO C falham consecutivamente.
 */
export const ECONOMY_MODE_CONFIG = {
  FAILURE_THRESHOLD: 3,           // Falhas consecutivas para ativar
  COOLDOWN_MS: 10 * 60 * 1000,   // 10 minutos de pausa
  JITTER_MAX_MS: 15 * 1000,      // Variação aleatória máxima
} as const;
